// Download Card Component
// New downloads section - entire card clickable, admin overlay when admin=true
// Placeholder structure for when API data arrives

import React from 'react';
import './DownloadCard.css';

const PLACEHOLDER_IMAGE = 'https://dummyimage.com/320x180/1a1a1a/666666&text=No+Image';

function safeImageUrl(url) {
  if (url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
    return url;
  }
  return PLACEHOLDER_IMAGE;
}

function truncateDescription(text, maxLen = 100) {
  if (!text) return '';
  const plain = String(text)
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/\n/g, ' ')
    .trim();
  return plain.length > maxLen ? `${plain.substring(0, maxLen)}...` : plain;
}

export function DownloadCard({ download, admin = false, sortMode = '', onClick, onPinToggle, onEdit, onDelete, draggable = false, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, isDragging = false, isDragOver = false }) {
  if (!download) return null;

  const isPinned = download.isPinned === true;
  // Only show pinned styling when using default sort (empty sortMode)
  const showPinnedStyling = isPinned && sortMode === '';
  const imageUrl = download.imageUrl || (download.images?.[0]?.url) || PLACEHOLDER_IMAGE;
  const safeUrl = safeImageUrl(imageUrl);
  const title = download.title || 'Untitled';
  const description = truncateDescription(download.description);
  const views = download.views ?? 0;
  const downloads = download.downloads ?? 0;

  const handleClick = (e) => {
    if (onClick) {
      onClick(download);
    }
  };

  const handlePinClick = (e) => {
    e.stopPropagation();
    if (onPinToggle) {
      onPinToggle(download);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(download);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(download);
    }
  };

  return (
    <div
      className={`download-card ${showPinnedStyling ? 'download-card--pinned' : ''} ${isDragging ? 'download-card--dragging' : ''} ${isDragOver ? 'download-card--drag-over' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      draggable={draggable && isPinned}
      onDragStart={draggable && isPinned ? (e) => onDragStart?.(e, download) : undefined}
      onDragOver={draggable && isPinned ? (e) => onDragOver?.(e, download) : undefined}
      onDragLeave={draggable && isPinned ? () => onDragLeave?.() : undefined}
      onDrop={draggable && isPinned ? (e) => onDrop?.(e, download) : undefined}
      onDragEnd={draggable && isPinned ? () => onDragEnd?.() : undefined}
    >
      {showPinnedStyling && (
        <div className="download-card-pinned-badge" title="Pinned">
          <i className="fa-solid fa-star"></i>
          <span>Pinned</span>
        </div>
      )}

      {draggable && isPinned && (
        <div className="download-card-drag-handle" title="Drag to reorder pinned items">
          <i className="fa-solid fa-grip-vertical"></i>
        </div>
      )}

      {admin && (
        <div className="download-card-admin-overlay">
          <button
            type="button"
            className={`download-card-admin-btn ${isPinned ? 'download-card-admin-btn--pinned' : ''}`}
            title={isPinned ? "Unpin" : "Pin"}
            aria-label={isPinned ? "Unpin" : "Pin"}
            onClick={handlePinClick}
          >
            <i className={isPinned ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
          </button>
          <button type="button" className="download-card-admin-btn" title="Edit" aria-label="Edit" onClick={handleEditClick}>
            <i className="fa-solid fa-pen"></i>
          </button>
          <button type="button" className="download-card-admin-btn download-card-admin-btn--delete" title="Delete" aria-label="Delete" onClick={handleDeleteClick}>
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      )}

      <div className="download-card-image">
        <img src={safeUrl} alt={title} className="download-card-image-src" onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }} />
      </div>

      <div className="download-card-details">
        <h3 className="download-card-title">{title}</h3>
        {description && <p className="download-card-description">{description}</p>}

        {/* Tags Row */}
        {Array.isArray(download.tags) && download.tags.length > 0 && (
          <div className="download-card-tags-row">
            {download.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="download-card-tag">
                {tag.charAt(0).toUpperCase() + tag.slice(1)}
              </span>
            ))}
            {download.tags.length > 3 && (
              <span className="download-card-tag download-card-tag--more" title={`${download.tags.length - 3} more tags`}>
                +{download.tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="download-card-meta">
          <span className="download-card-stat">
            <i className="fa-solid fa-eye" aria-hidden></i> {views}
          </span>
          <span className="download-card-stat">
            <i className="fa-solid fa-download" aria-hidden></i> {downloads}
          </span>
        </div>
      </div>
    </div>
  );
}
